import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import { useCollectionTutorialsState } from "@/features/collection_home";
import type { AdaptationListAsset } from "@/features/collection_home/types";
import { formatStringOrNotSupplied, type HazardPlusTotal, type Scenario, type Year } from "@/features/pe_aldd";
import { type AbcdeRiskValue, riskValueThresholds } from "@/features/risk_bands";
import { ADAPTATION_TERM_COLORS } from "@/types/adaptation";
import { formatAbbreviatedValueWithMetricUnit } from "@/utils/number";
import { getAnalysisTypeName } from "@/utils/string";
import { CloseCircleFilled } from "@ant-design/icons";
import { SortAscendingIcon, SortDescendingIcon } from "@phosphor-icons/react/dist/ssr";
import { Button, Input, Popover, Select, Tag, Tooltip, Typography } from "antd";
import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { computeLabelColumnWidth, type Domain, expandDomainToRoundBounds, extentOfDumbbells } from "../../collection_home/components/charts/common/dumbbellChartGeometry";
import { computeAbsoluteChange, computeRelativeChange, getMetricShortLabel, isHazardMetricDataAvailable, MetricKey, selectTopPercentileKeys, unitForMetric } from "../../collection_home/components/charts/common/utils";
import { DumbbellChart } from "../../collection_home/components/charts/DumbellChart";
import { DumbellRangeFilter } from "../../collection_home/components/charts/DumbellRangeFilter";
import CollectionFiltersLabel from "../../collection_home/components/CollectionFiltersLabel";
import SelectMetricOptions from "../../collection_home/components/SelectMetricOptions";
import { extractPointMetricsForSingleResult } from "../../collection_home/components/utils/pointToSummary";
import { useMetricOptions } from "../../collection_home/hooks/useMetricOptions";
import { useDataColors } from "../useDataColors";
type MetricGroup = Partial<Record<MetricKey, number | null>>;
const {
  Title
} = Typography;
interface AdaptedRow {
  key: string;
  displayInput: string;
  adapted: MetricGroup & {
    id: string;
  };
  baseline: MetricGroup & {
    id: string;
  };
}
type AdaptationSortKey = "alphabetical" | "baseline" | "enhanced_resilience" | "relativeChange" | "absoluteChange";
type SortOrder = "asc" | "desc";
const DEFAULT_ADAPTATION_SORT_KEY: AdaptationSortKey = "baseline";
const DEFAULT_ADAPTATION_SORT_DIRECTION: SortOrder = "desc";
const SORT_ORDER_LABELS: Record<SortOrder, string> = {
  asc: "Ascending",
  desc: "Descending"
};

/** Shared empty set, so clearing exclusions does not hand the memos a fresh identity each time. */
const NO_EXCLUSIONS: ReadonlySet<string> = new Set();
const DirectionIcon = ({
  order,
  size = 12
}: {
  order: SortOrder;
  size?: number;
}) => order === "asc" ? <SortAscendingIcon size={size} /> : <SortDescendingIcon size={size} />;
export const SidebarAdaptedList = ({
  adaptationAssets,
  hasAdaptation,
  rcp,
  activeHazard,
  year,
  regionType,
  searchQuery,
  onSelect,
  onHighlight,
  highlightedId,
  errorMessage,
  tooltipContainerId
}: {
  adaptationAssets: AdaptationListAsset[];
  hasAdaptation: boolean;
  activeHazard: HazardPlusTotal;
  rcp: Scenario;
  year: Year;
  regionType: string | undefined;
  searchQuery: string;
  onSelect: (pointId: string) => void;
  onHighlight: (pointId: string) => void;
  highlightedId?: string | null;
  errorMessage?: React.ReactNode;
  tooltipContainerId?: string;
}) => {
  const {
    t
  } = useTranslation("terms");
  const {
    t: tTabs
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.tabs"
  });
  const {
    t: tCharts
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.assetRisk"
  });
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.hazardBreakdown"
  });
  const {
    ratings
  } = useDataColors();
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const adaptationSortOptions: {
    label: string;
    key: AdaptationSortKey;
    contextId?: string;
  }[] = [{
    label: "Alphabetical",
    key: "alphabetical"
  }, {
    label: `${t("adaptationTypes.baseline")} Risk`,
    key: "baseline",
    contextId: "baselineAdaptation"
  }, {
    label: `${t("adaptationTypes.enhanced_resilience")} Risk`,
    key: "enhanced_resilience",
    contextId: "autoEnhancedResilience"
  }, {
    label: `${t("relativeChange")}`,
    key: "relativeChange",
    contextId: "relativeChange"
  }, {
    label: `${t("absoluteChange")}`,
    key: "absoluteChange",
    contextId: "absoluteChange"
  }];
  const [metric, setMetric] = useState<MetricKey>("avg_damage_mtd");
  const [excludedKeys, setExcludedKeys] = useState<ReadonlySet<string>>(NO_EXCLUSIONS);
  const [manualRange, setManualRange] = useState<Domain | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);

  // A metric switch changes what counts as an outlier. Compared during render rather than inside
  // handleMetricChange, which useMetricOptions bypasses when it falls back to a permitted metric.
  const [lastMetric, setLastMetric] = useState(metric);
  if (lastMetric !== metric) {
    setLastMetric(metric);
    setExcludedKeys(NO_EXCLUSIONS);
  }

  // reset selected chart rage on collection filters/metric change
  const datasetKey = [metric, activeHazard, rcp, year, regionType].join("|");
  const [lastDatasetKey, setLastDatasetKey] = useState(datasetKey);
  if (lastDatasetKey !== datasetKey) {
    setLastDatasetKey(datasetKey);
    setManualRange(null);
  }
  const {
    metricOptions,
    selectedMetric
  } = useMetricOptions({
    metric,
    setMetric,
    showTtip: true,
    isPointLevel: true,
    translationPrefix: "portfolios.pointLevel.assetRisk"
  });
  const hazardDataAvailable = isHazardMetricDataAvailable(activeHazard, metric);
  const hazardDataUnavailableMessage = tCharts(`charts.${getMetricShortLabel(metric).toLowerCase()}.unavailableMessage`, tHazardBreakdown("unavailableMessage"));
  const [adaptationSortKey, setAdaptationSortKey] = useState<AdaptationSortKey>(DEFAULT_ADAPTATION_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortOrder>(DEFAULT_ADAPTATION_SORT_DIRECTION);
  const handleMetricChange = (newMetric: MetricKey) => {
    setMetric(newMetric);
    setAdaptationSortKey(DEFAULT_ADAPTATION_SORT_KEY);
    setSortDirection(DEFAULT_ADAPTATION_SORT_DIRECTION);
  };
  const selectedSortOption = adaptationSortOptions.find(o => o.key === adaptationSortKey);
  const sortSelectOptions = adaptationSortOptions.map(o => ({
    value: o.key,
    label: o.contextId ? <div className="flex w-full items-center justify-start gap-2">
        <span>{o.label}</span>
        <ContextualInfoButton className="!text-xs" id={o.contextId} />
      </div> : o.label
  }));
  const toggleSortDirection = () => setSortDirection(previous => previous === "asc" ? "desc" : "asc");
  const rows = useMemo<AdaptedRow[]>(() => {
    if (!hasAdaptation) return [];
    const byPortfolioAssetId = new Map<string, {
      adapted?: AdaptationListAsset;
      baseline?: AdaptationListAsset;
    }>();
    for (const asset of adaptationAssets) {
      const portfolioAssetId = asset.portfolio_asset_id;
      if (!portfolioAssetId) continue;
      if (!byPortfolioAssetId.has(portfolioAssetId)) byPortfolioAssetId.set(portfolioAssetId, {});
      const group = byPortfolioAssetId.get(portfolioAssetId)!;
      if (asset.adaptation_type === "enhanced_resilience") group.adapted = asset;else if (asset.adaptation_type === "baseline") group.baseline = asset;
    }
    return Array.from(byPortfolioAssetId.values()).filter(({
      adapted,
      baseline
    }) => adapted != null && baseline != null).map(({
      adapted,
      baseline
    }) => ({
      key: baseline!.uuid,
      displayInput: formatStringOrNotSupplied(baseline!.data?.userSuppliedId, baseline!.uuid),
      adapted: extractPointMetricsForSingleResult(adapted!, rcp, activeHazard, year, regionType),
      baseline: extractPointMetricsForSingleResult(baseline!, rcp, activeHazard, year, regionType)
    }));
  }, [adaptationAssets, hasAdaptation, activeHazard, rcp, year, regionType]);
  const {
    computedRows,
    hasTrophies,
    noDataCount
  } = useMemo(() => {
    // drop rows with null value for selected metric
    const usable = rows.filter(row => row.baseline[metric] != null && row.adapted[metric] != null);
    const computed = usable.map(row => {
      const baselineValue = row.baseline[metric]!;
      const adaptedValue = row.adapted[metric]!;
      return {
        ...row,
        baselineValue,
        adaptedValue,
        relativeChange: computeRelativeChange(baselineValue, adaptedValue, unitForMetric(metric)),
        absChange: computeAbsoluteChange(baselineValue, adaptedValue)
      };
    });

    // No trophies while the metric has no data for this hazard — there is no change to rank.
    const topKeys = hazardDataAvailable ? selectTopPercentileKeys(computed) : new Set<string>();
    return {
      computedRows: computed.map(row => ({
        ...row,
        isTopDecile: topKeys.has(row.key)
      })),
      hasTrophies: topKeys.size > 0,
      noDataCount: rows.length - usable.length
    };
  }, [rows, metric, hazardDataAvailable]);

  // prevent trophy re-rank on row exclude
  const includedRows = useMemo(() => computedRows.filter(row => !excludedKeys.has(row.key)), [computedRows, excludedKeys]);

  // preserve excluding keys on collection filters change
  const excludedCount = computedRows.length - includedRows.length;
  const sortedRows = useMemo(() => {
    // null = no data available — sorts last in BOTH directions, so the null branches
    // must sit outside the direction flip below.
    const valueOf = (row: (typeof computedRows)[number]): number | string | null => {
      switch (adaptationSortKey) {
        case "alphabetical":
          return row.displayInput;
        case "baseline":
          return row.baselineValue;
        case "enhanced_resilience":
          return row.adaptedValue;
        case "relativeChange":
          return row.relativeChange;
        case "absoluteChange":
        default:
          // absChange is adapted - baseline, so reductions are negative; negate to match relativeChange's sign.
          return row.absChange === null ? null : -row.absChange;
      }
    };
    return [...computedRows].sort((a, b) => {
      const aValue = valueOf(a);
      const bValue = valueOf(b);
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      const cmp = typeof aValue === "string" ? aValue.localeCompare(bValue as string, undefined, {
        numeric: true,
        sensitivity: "base"
      }) : aValue - (bValue as number);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [computedRows, adaptationSortKey, sortDirection]);
  const chartData = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return sortedRows.filter(row => row.displayInput.toLowerCase().includes(query)).map(row => ({
      key: row.key,
      name: row.displayInput,
      start: row.baselineValue,
      end: row.adaptedValue,
      isTopDecile: row.isTopDecile,
      isExcluded: excludedKeys.has(row.key)
    }));
  }, [sortedRows, searchQuery, excludedKeys]);

  // Exclusions rescale the axis. The search filter on chartData deliberately does not: this tab has
  // no search box, so the query is inherited from risk/exposure and nothing on screen explains it.
  const autoDomain = useMemo(() => expandDomainToRoundBounds(extentOfDumbbells(includedRows.map(r => ({
    start: r.baselineValue,
    end: r.adaptedValue
  })))), [includedRows]);

  // range for all asset with data, not just the clamped chart range
  const rangeBounds = useMemo(() => expandDomainToRoundBounds(extentOfDumbbells(computedRows.map(r => ({
    start: r.baselineValue,
    end: r.adaptedValue
  })))), [computedRows]);

  // 'excluded asset' range deduction ignored when explicit range is selected
  const domain = manualRange ?? autoDomain;

  // Measured before exclusions, so restoring an asset cannot widen the column and shift the plot.
  const labelColumnWidth = useMemo(() => computeLabelColumnWidth(computedRows.map(r => r.displayInput)), [computedRows]);

  // reset scroll on filter/sort/metric change
  const scrollResetKey = [activeHazard, rcp, year, regionType, metric, adaptationSortKey, sortDirection, searchQuery].join("|");
  const {
    bindAdaptationChart,
    bindAdaptationListSort,
    bindAdaptationRow,
    selectFirstItemInAssetList,
    isTutorialOpen
  } = useCollectionTutorialsState();
  const handleToggleExclude = (key: string) => setExcludedKeys(previous => {
    const next = new Set(previous);
    if (!next.delete(key)) next.add(key);
    return next;
  });
  const handleRestoreAll = () => setExcludedKeys(NO_EXCLUSIONS);

  // 'preventing exclude' only applies to included rows
  const excludeDisabledReason = includedRows.length <= 1 ? "At least one asset must stay on the chart" : undefined;
  useEffect(() => {
    if (isTutorialOpen && selectFirstItemInAssetList && chartData.length > 0) {
      onSelect(chartData[0].key);
    }
  }, [selectFirstItemInAssetList]);
  const riskColorSections = useMemo(() => {
    if (metric !== "avg_damage_mtd") return [];
    const [min, max] = domain;
    const letters = Object.keys(riskValueThresholds) as AbcdeRiskValue[];
    return letters.map((letter, i) => {
      const next = letters[i + 1];
      const lo = riskValueThresholds[letter] * 100;
      const hi = next ? riskValueThresholds[next] * 100 : Number.MAX_SAFE_INTEGER;
      return {
        from: Math.max(lo, min),
        to: Math.min(hi, max),
        color: ratings[letter]
      };
    }).filter(s => s.to > s.from);
  }, [metric, domain, ratings]);
  if (!metricOptions.length || !selectedMetric) return null;
  if (errorMessage) {
    return <div className="animate-fade-in-slide flex h-full flex-col overflow-hidden">
        <div className="p-2">{errorMessage}</div>
      </div>;
  }
  const formatRange = ([min, max]: Domain) => `${formatAbbreviatedValueWithMetricUnit(min, selectedMetric.unit)} – ${formatAbbreviatedValueWithMetricUnit(max, selectedMetric.unit)}`;
  return (
    // The tour highlight is sized to this rect; the chart inside grows with the asset count.
    <div ref={node => {
      setScrollElement(node);
      bindAdaptationChart(node);
    }} className="animate-fade-in-slide h-full overflow-y-auto overflow-x-hidden" data-sentry-component="SidebarAdaptedList" data-sentry-source-file="SidebarAdaptedList.tsx">
      <div ref={headerRef}>
        <Title className="!mb-2 px-2 pt-4" level={4} data-sentry-element="Title" data-sentry-source-file="SidebarAdaptedList.tsx">
          All {getAnalysisTypeName(false, true)}
        </Title>

        <ContextualInfoCollapse className="mx-2 mb-2" data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="SidebarAdaptedList.tsx">
          <Trans i18nKey={tTabs(`${"adaptation"}.note`)} values={{
            baseline: t("adaptationTypes.baseline"),
            enhancedResilience: t("adaptationTypes.enhanced_resilience")
          }} components={{
            span: <strong />
          }} data-sentry-element="Trans" data-sentry-source-file="SidebarAdaptedList.tsx" />
        </ContextualInfoCollapse>

        <div ref={bindAdaptationListSort} className="flex-shrink-0 border-y border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex items-center gap-1.5">
            <SelectMetricOptions variant="select" metric={metric} setMetric={handleMetricChange} metricOptions={metricOptions} selectClassName="flex-1" data-sentry-element="SelectMetricOptions" data-sentry-source-file="SidebarAdaptedList.tsx" />

            <Select value={adaptationSortKey} onChange={setAdaptationSortKey} options={sortSelectOptions} popupMatchSelectWidth={false} classNames={{
              root: "max-w-[320px]",
              prefix: "border-r -ml-1 pr-1 border-subtle"
            }} className="min-w-0 flex-1" title={`Sort by ${selectedSortOption?.label ?? ""}`} labelRender={() => <span className="flex min-w-0 items-center gap-1">
                  <span className="min-w-0 truncate">{selectedSortOption?.label}</span>
                  {selectedSortOption?.contextId && <ContextualInfoButton id={selectedSortOption.contextId} className="shrink-0 !text-xs" />}
                </span>} prefix={<Button type="text" size="small" iconPlacement="end" icon={<DirectionIcon order={sortDirection} size={14} />} aria-label={`Sort ${SORT_ORDER_LABELS[sortDirection].toLowerCase()}, click to reverse`} title={SORT_ORDER_LABELS[sortDirection]} className="!text-gray-700"
            // The select opens on mousedown anywhere inside it, so both events stop here.
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
            }} onClick={e => {
              e.stopPropagation();
              toggleSortDirection();
            }}>
                  Sort by:
                </Button>} data-sentry-element="Select" data-sentry-source-file="SidebarAdaptedList.tsx" />
          </div>

          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="shrink-0 text-xs text-gray-500">Adjust metric range:</span>
            <Popover trigger="click" placement="bottomRight" open={rangeOpen} onOpenChange={setRangeOpen} title={`${selectedMetric.shortLabel} (${selectedMetric.unit}) range`} content={<DumbellRangeFilter bounds={rangeBounds} value={domain} isManual={manualRange !== null} unit={selectedMetric.unit} onChange={range => {
              setManualRange(range);
              setRangeOpen(false);
            }} onReset={() => setManualRange(null)} />} data-sentry-element="Popover" data-sentry-source-file="SidebarAdaptedList.tsx">
              <div className={classNames("max-w-[300px] flex-1 cursor-pointer", {
                "pointer-events-none": !hazardDataAvailable
              })}>
                <Input readOnly size="small" disabled={!hazardDataAvailable} value={formatRange(domain)} prefix={<span className="text-xs text-gray-700">
                      {selectedMetric.shortLabel} ({selectedMetric.unit})
                    </span>}
                // antd hides allowClear on readOnly inputs, so the reset is hand-rolled.
                suffix={<Tooltip title={manualRange ? "Reset range" : "Default range cannot be cleared"}>
                      <CloseCircleFilled type="button" disabled={!manualRange} aria-label={manualRange ? "Reset range" : "Default range cannot be cleared"} title="Reset range" className={`flex cursor-pointer items-center border-0 bg-transparent p-0 text-xs ${manualRange ? "text-black/25 hover:text-black/45" : "text-black/10"}`} onClick={e => {
                    // Keep the click from reaching the wrapper and opening the popover.
                    e.stopPropagation();
                    setManualRange(null);
                  }} />
                    </Tooltip>} classNames={{
                  input: "!cursor-pointer"
                }} data-sentry-element="Input" data-sentry-source-file="SidebarAdaptedList.tsx" />
              </div>
            </Popover>
          </div>

          {/* Grey text states a fact about the data; a closable tag is something the reader did. */}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 empty:hidden" role="status" aria-live="polite">
            {noDataCount > 0 && <span className="text-xs text-gray-500">
                {noDataCount} {noDataCount === 1 ? "asset" : "assets"} hidden — no {selectedMetric.shortLabel} data
              </span>}
            {excludedCount > 0 && <Tag variant="outlined" closable onClose={handleRestoreAll} className="!mr-0 !text-xs">
                {excludedCount} excluded from chart
              </Tag>}
          </div>

          {includedRows.length === 0 && computedRows.length > 0 && <div className="mt-1.5 text-xs text-gray-500">
              Every asset is excluded.{" "}
              <Button type="link" size="small" onClick={handleRestoreAll} className="!h-auto !p-0 !text-xs">
                Restore all
              </Button>{" "}
              to plot them again.
            </div>}
        </div>
      </div>

      <DumbbellChart data={chartData} domain={domain} onSelect={onSelect} onHighlight={onHighlight} onToggleExclude={hazardDataAvailable ? handleToggleExclude : undefined} excludeDisabledReason={excludeDisabledReason} highlightedKey={highlightedId} startLabel={t("adaptationTypes.baseline")} endLabel={t("adaptationTypes.enhanced_resilience")} startColor={ADAPTATION_TERM_COLORS.baseline} endColor={ADAPTATION_TERM_COLORS.enhanced_resilience} dataUnit={selectedMetric.unit} showCustomAdaptationTooltip tooltipFooter={<CollectionFiltersLabel />} tooltipContainerId={tooltipContainerId} xAxisLabel={`${selectedMetric.shortLabel} (${selectedMetric.unit})`} tooltipValueLabel={selectedMetric.shortLabel} scrollElement={scrollElement} headerHeight={headerHeight} labelColumnWidth={labelColumnWidth} scrollResetKey={scrollResetKey} riskColorSections={riskColorSections} hasTrophies={hasTrophies} firstRowRef={bindAdaptationRow} dataAvailable={hazardDataAvailable} unavailableMessage={hazardDataUnavailableMessage} data-sentry-element="DumbbellChart" data-sentry-source-file="SidebarAdaptedList.tsx" />
    </div>
  );
};